import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { VirtualBrand } from '~/Api/Analytics/VirtualBrand';
import { AnalyticDataSources, VirtualPackBrandAnalytics } from '~/Api/Analytics/VirtualBrand/mapAnalyticVirtualBrand';
import { ApplicationState, ThunkDispatch } from '~/Store';

import { fetchVirtualBrandAnalytics } from './actions';

import BrandDetail, { type Props as BrandDetailProps } from './BrandDetail';

type OwnProps = Omit<
	BrandDetailProps,
	| 'onLoad'
	| 'virtualBrandAvailability'
	| 'virtualPackAvailability'
	| 'fetchingVirtualBrandAnalytics'
	| 'subscriptionModules'
>;

const getVirtualPackAvailabilities = (
	virtualPacks: VirtualPackBrandAnalytics[] | null
): { [virtualPackId: string]: { [countryCode: string]: AnalyticDataSources } } | null => {
	if (!virtualPacks) {
		return null;
	}

	return virtualPacks.reduce(
		(
			aggregator: { [virtualPackId: string]: { [countryCode: string]: AnalyticDataSources } },
			virtualPackBrandAnalytics: VirtualPackBrandAnalytics
		) => {
			const byVirtualPackId = aggregator[virtualPackBrandAnalytics.id] || {};

			return {
				...aggregator,
				[virtualPackBrandAnalytics.id]: virtualPackBrandAnalytics.dataSources.reduce(
					(aggregator, { dataSource, country, hasPrice }) => {
						return {
							...aggregator,
							[country.alpha2]: (aggregator[country.alpha2] || []).concat([{ dataSource, country, hasPrice }]),
						};
					},
					byVirtualPackId
				),
			};
		},
		{}
	);
};

const getVirtualBrandAvailabilities = (
	virtualPacks: VirtualPackBrandAnalytics[] | null
): { [countryCode: string]: AnalyticDataSources } | null => {
	if (!virtualPacks) {
		return null;
	}

	return virtualPacks.reduce(
		(
			aggregator: { [countryCode: string]: AnalyticDataSources },
			virtualPackBrandAnalytics: VirtualPackBrandAnalytics
		) => {
			return virtualPackBrandAnalytics.dataSources.reduce((countryAggregator, { dataSource, country, hasPrice }) => {
				return {
					...countryAggregator,
					[country.alpha2]: (aggregator[country.alpha2] || []).concat([{ dataSource, country, hasPrice }]),
				};
			}, aggregator);
		},
		{}
	);
};

const memoizedGetVirtualPackAvailabilities = memoizeOne(getVirtualPackAvailabilities);
const memoizedGetVirtualVrandAvailabilities = memoizeOne(getVirtualBrandAvailabilities);

export const selectVirtualBrandAnalytics = memoizeOne(
	(
		virtualBrandId: string,
		medicinalBrandsAnalyticsVirtualBrand: ApplicationState['medicinalBrandsAnalyticsVirtualBrand']
	) => medicinalBrandsAnalyticsVirtualBrand.analyticsVirtualBrands?.items?.get(virtualBrandId) || null
);

export const selectVirtualBrand = memoizeOne(
	(
		virtualBrandId: string,
		medicinalBrandsAnalyticsVirtualBrand: ApplicationState['medicinalBrandsAnalyticsVirtualBrand']
	) =>
		medicinalBrandsAnalyticsVirtualBrand.virtualBrands?.items?.find((candidate) => candidate.id === virtualBrandId) ||
		null
);

const mapStateToProps = (
	{ accessToken, medicinalBrandsAnalyticsVirtualBrand }: ApplicationState,
	props: OwnProps
): {
	fetchingVirtualBrandAnalytics: boolean;
	subscriptionModules: Set<string>;
	virtualBrandAvailability: { [countryCode: string]: AnalyticDataSources } | null;
	virtualPackAvailability: { [virtualPackId: string]: { [countryCode: string]: AnalyticDataSources } } | null;
} => {
	const analyticVirtualBrandData = selectVirtualBrandAnalytics(
		props.virtualBrand.id,
		medicinalBrandsAnalyticsVirtualBrand
	);
	const subscriptionModules = accessToken.accessToken?.subscription?.systemModules || new Set();

	return {
		fetchingVirtualBrandAnalytics: Boolean(
			medicinalBrandsAnalyticsVirtualBrand.analyticsVirtualBrands?.fetchedBrands?.has(props.virtualBrand.id)
		),
		subscriptionModules,
		virtualBrandAvailability: memoizedGetVirtualVrandAvailabilities(analyticVirtualBrandData?.virtualPacks || null),
		virtualPackAvailability: memoizedGetVirtualPackAvailabilities(analyticVirtualBrandData?.virtualPacks || null),
	};
};

const mapDispatchToProps = (dispatch: ThunkDispatch<AnyAction>) => {
	return {
		onLoad(virtualBrand: VirtualBrand): void {
			dispatch(fetchVirtualBrandAnalytics(virtualBrand.id));
		},
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(BrandDetail);
